import { ChatChannelContext } from './ChatChannel';
import zod from 'zod';
import { useCallback, useReducer, useEffect, useContext } from 'react';
import { RichText, Root } from '@aidkitorg/types/lib/survey';
import { crawlSurveyKinds } from '@aidkitorg/types/lib/traverse';
import { findComponent, TreeBlock } from '../ConfigSearch';

export type QuestionState = {
  sectionId: string,
  visible: boolean,
  content: string,
  type: string,
  targetField: string,
  options?: {
    name: string,
    content: RichText,
    terminatesApplication?: boolean,
    fieldType: 'custom-option' | 'select-all' | 'other',
  }[],
  completed?: boolean,
  validity?: {
    state: 'unentered' | 'valid' | 'invalid',
    message?: string
  }
};

export type QuestionReducerState = {
  questions: Record<string, Partial<QuestionState>>
}

export type QuestionStateEvent = {
  type: 'change',
  questionId: string,
  update: Partial<QuestionState>
};

export function useQuestionStateReducer(initial?: QuestionReducerState) {
  const chat = useContext(ChatChannelContext);

  const [state, dispatch] = useReducer((state: QuestionReducerState, event: QuestionStateEvent) => {
    return {
      ...state,
      questions: {
        ...state.questions,
        [event.questionId]: {
          ...(state.questions[event.questionId] ?? {}),
          ...event.update
        }
      }
    };
  }, initial ?? { questions: {} });

  const questionChanged = useCallback((event: QuestionStateEvent) => {
    dispatch(event);
  }, [dispatch]);

  useEffect(() => {
    if (chat.registerTool && chat.enabled && chat.channelIsReady) {
      chat.registerTool({
        name: 'list_questions',
        description: `list questions and their current state in the survey. questions are not returned in any particular ordering.`,
        schema: zod.object({
          visibleOnly: zod.boolean({ description: `
            if true, only returns questions that are currently visible on the user's screen.
          ` }),
          completionStatus: zod.nullable(zod.enum(['complete', 'incomplete'])),
        }),
        async fn({ visibleOnly, completionStatus }) {
          return Object.values(state.questions).filter(q => 
            (!visibleOnly || q.visible) &&
            (!completionStatus || (q.completed && completionStatus === 'complete') || (!q.completed && completionStatus === 'incomplete'))
          );
        }
      });
    }

  }, [chat.enabled, chat.channelIsReady, chat.registerTool, state.questions]);

  return {
    questionChanged
  };
}

export function useDistroTools(props: { survey: Root, channel: React.ContextType<typeof ChatChannelContext> }) {

  useEffect(() => {
    if (props.channel.registerTool) {
      console.debug('initializing distro chat tools...');

      props.channel.registerTool({
        name: 'list_active_distro_kinds',
        description: `
          list all of the distro kinds utilized in the current configuration.
            
          ## Examples
          
          ### Asking Plainly for Kind Usage
          User: what kinds are used in this survey?
          Tool Call Args: {}
          
        `,
        schema: zod.object({}),
        async fn() {
          const kinds = new Set<string>();
          crawlSurveyKinds(props.survey, item => item.kind && kinds.add(item.kind));
          return Array.from(kinds);
        }
      });

      props.channel.registerTool({
        name: 'find_kind',
        description: `
        search within the configuration for a specific distro kind.

        ## Examples
        
        User: please find all payments
        Tool Call Args: { "searchTerm": "payment", "limitToKind": true }
      `,
        schema: zod.object({
          kind: zod.string({ description: 'the kind to search for' }),
        }),
        async fn({ kind }) {
          let survey = props.survey;
          if (!Array.isArray(survey)) {
            survey = survey.survey;
          }

          const components = (await findComponent(survey, kind, {
            includeDashboards: true,
            wholeMatchOnly: false,
            matchCase: false
          }))
            ?.filter(c => c.thing.kind.toLowerCase() === kind.toLowerCase())
            ?.map(c => JSON.parse(JSON.stringify(c.thing,
              (_, value) => typeof value === 'object' && !Array.isArray(value) && 'en' in value ? value['en'] : value)
            ))

          return components;
        }
      });

      props.channel.registerTool({
        name: 'find_by_keyword',
        description: `
        search within the configuration for a specific keyword. this should be the last thing used, other tools are more specific.

        ## Examples
        
        ### Searching for Target Fields
        User: where is payment_1_ok used
        Tool Call Args: { "searchTerm": "payment_1_ok", "matchCase": true, "wholeMatchOnly": true, "includeDashboards": true }
      `,
        schema: zod.object({
          searchTerm: zod.string({ description: 'the term to search with' }),
          includeDashboards: zod.boolean({ description: 'include dashboard types in the search' }),
          wholeMatchOnly: zod.boolean({ description: 'match the whole search term exactly' }),
          matchCase: zod.boolean({ description: 'filter matches to the exact casing of the search term' }),
        }),
        async fn({ searchTerm, includeDashboards, wholeMatchOnly, matchCase }) {
          let survey = props.survey;
          if (!Array.isArray(survey)) {
            survey = survey.survey;
          }
          let components: TreeBlock[] = await findComponent(survey, searchTerm, {
            includeDashboards,
            wholeMatchOnly,
            matchCase
          }) ?? [];

          return components;
        }
      })
    }
  }, [props.survey, props.channel.registerTool]);
}
